import React, { useState, useEffect } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  Text,
} from '@chakra-ui/react';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';
import AccountService from '../../services/AccountService.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { useManifest } from '../../context/ManifestContext.tsx';
import { ICreateAccountPayload } from '../../interfaces/Account/ICreateAccountPayload.ts';
import { IUpdateAccountPayload } from '../../interfaces/Account/IUpdateAccountPayload.ts';
import { IAccount } from '../../interfaces/Account/IAccount.ts';
import { useTranslation } from 'react-i18next';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import { useToast } from '@chakra-ui/react';
import AleaInput from '../../components/AleaInput/AleaInput.tsx';
import { generateLangForPayload } from '../../utils/generateLangForPayload.ts';

interface AddAppAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  carehomeId?: string;
  caregiver?: IAccount | null;
}

const AddAppAccountModal: React.FC<AddAppAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carehomeId,
  caregiver,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { languages: manifestLanguages } = useManifest();
  const toast = useToast();
  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const trimmedUsername = username.trim();
  const isUsernameEmpty = trimmedUsername.length === 0;
  const isUsernameTooLong = trimmedUsername.length > 20;
  const isUsernameValid = !isUsernameEmpty && !isUsernameTooLong;
  const activeLanguageCode = generateLangForPayload(
    i18n.resolvedLanguage || i18n.language,
    manifestLanguages
  );

  const isEditMode = !!caregiver;

  // Reset / initialize form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (caregiver) {
        setUsername(caregiver.username || caregiver.first_name || '');
      } else {
        setUsername('');
      }
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
      setServerError(null);
    } else {
      setUsername('');
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
      setServerError(null);
    }
  }, [isOpen, caregiver]);

  // Ensure header/backdrop z-index behaves correctly while modal is open
  useEffect(() => {
    try {
      const ev = new CustomEvent('alea-global-modal-open', { detail: isOpen });
      window.dispatchEvent(ev);
    } catch {}
    return () => {
      try {
        const ev = new CustomEvent('alea-global-modal-open', { detail: false });
        window.dispatchEvent(ev);
      } catch {}
    };
  }, [isOpen]);

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    setServerError(null);
    if (!isUsernameValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      if (isEditMode && caregiver) {
        const payload: IUpdateAccountPayload = {
          organisation_id: caregiver.organisation_id ?? null,
          carehome_id: caregiver.carehome_id || carehomeId,
          first_name: trimmedUsername,
          last_name: caregiver.last_name,
          middle_name: caregiver.middle_name || undefined,
          position: caregiver.position || undefined,
          compound_mobile_phone_number: caregiver.compound_mobile_phone_number ?? null,
          compound_phone_number: caregiver.compound_phone_number ?? null,
          email: caregiver.email,
          image: caregiver.image || undefined,
          username: trimmedUsername,
          last_login: caregiver.last_login || undefined,
          role: caregiver.role,
          is_active: caregiver.is_active,
          lang: caregiver.lang,
          title: caregiver.title || undefined,
        };

        await AccountService.updateAccount(caregiver.id, payload);
      } else {
        const payload: ICreateAccountPayload = {
          organisation_id: user?.organisation?.id || '',
          carehome_id: carehomeId || user?.carehome?.id || '',
          first_name: trimmedUsername,
          last_name: '',
          email: '',
          username: trimmedUsername,
          password: '',
          role: 'Nursing professional',
          is_active: true,
          lang: activeLanguageCode,
        };

        await AccountService.createAccount(payload);
      }

      onConfirm?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to save app account', error);

      // For create flow: show friendly "name already taken" error (no toast) when 400
      if (!isEditMode) {
        const status = error?.response?.status;
        if (status === 400) {
          setServerError(t('errorNameAlreadyTaken'));
          return;
        }
      }

      toast({
        title: t('error'),
        description: isEditMode ? t('caregiver.editFailed') : t('careHome.caregiverAddFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay zIndex={1500} />
        <ModalContent
          data-testid="add-app-account-modal"
          minWidth="500px"
          zIndex={1501}
          borderRadius="20px"
          bg="white"
        >
          <ModalHeader
            color="black"
            fontSize="25px"
            fontWeight={500}
            textAlign="left"
            px="50px"
            pt="50px"
            pb="0"
          >
            <Text
              as="div"
              data-testid="add-app-account-modal-title"
              data-mode={isEditMode ? 'edit' : 'new'}
            >
              {t(isEditMode ? 'editAppAccount' : 'newAppAccount')}
            </Text>
            {isEditMode && (
              <Text
                data-testid="add-app-account-modal-edit-warning"
                mt="3px"
                color="invalid.500"
                fontSize="15px"
                fontWeight={300}
              >
                {t('editCaregiverWarning')}
              </Text>
            )}
          </ModalHeader>
          <ModalBody p="50px">
            {/* Safari autocomplete workaround */}
            <div style={{ height: 0, overflow: 'hidden' }}>
              <input type="text" name="fake_username" tabIndex={-1} />
              <input type="password" name="fake_password" tabIndex={-1} />
            </div>
            <AleaInput
              label={t('userNameCaregiver')}
              placeholder={t('caregiverUsernameExample')}
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (serverError) {
                  setServerError(null);
                }
              }}
              register={{
                name: 'app_account_username',
                onChange: async (_e: any) => {},
                onBlur: async (_e: any) => {},
                ref: () => {},
              }}
              error={
                serverError ||
                (hasAttemptedSubmit
                  ? isUsernameEmpty
                    ? t('requiredField', { field: t('userNameCaregiver') })
                    : isUsernameTooLong
                      ? t('errorNameLength')
                      : undefined
                  : undefined)
              }
              isRequired
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isUsernameValid) {
                  handleSubmit();
                }
              }}
              autoComplete="new-password"
            />
          </ModalBody>
          <ModalFooter pt={0} pb="50px" px="50px">
            <Flex justifyContent="flex-end" alignItems="center" width="100%">
              <Button
                data-testid="add-app-account-modal-cancel"
                variant="link"
                onClick={onClose}
                color="alea.500"
                textDecoration="underline"
                fontSize="18px"
                fontWeight={400}
              >
                {t('general.cancel')}
              </Button>
              <Button
                data-testid="add-app-account-modal-save"
                colorScheme="alea"
                variant="solid"
                onClick={handleSubmit}
                h="50px"
                w="100px"
                borderRadius="20px"
                px="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                ml="25px"
                isLoading={isSubmitting}
              >
                <AleaCheckIcon color="white" size={29} />
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {isSubmitting && <AleaLoader />}
    </>
  );
};

export default AddAppAccountModal;


